import { CLARIFY_ITEM_ID_PATTERN, SCADA_FEED_LINE_MAX } from '@/domain/providerConfig';

/** One SCADA entry as the editor holds it: every part as text, blank while unfilled. */
export interface ScadaRow {
    itemId: string;
    signal: string;
    feedLine: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringOr(value: unknown): string {
    return typeof value === 'string' ? value : '';
}

/**
 * The rows a stored list holds. Lenient on purpose: a malformed entry still shows as a row
 * so it can be fixed or removed, and the field's validation names the problem.
 */
export function parseScadaRows(text: string): ScadaRow[] {
    if (text.trim() === '') return [];
    let parsed: unknown;
    try {
        parsed = JSON.parse(text);
    } catch {
        return [];
    }
    if (!Array.isArray(parsed)) return [];
    return parsed.map((entry) =>
        isRecord(entry)
            ? {
                  itemId: stringOr(entry.item_id),
                  signal: stringOr(entry.signal),
                  feedLine: stringOr(entry.feed_line),
              }
            : { itemId: '', signal: '', feedLine: '' }
    );
}

/**
 * The field's text for `rows`: the entry shape the server stores and #483's ingest reads,
 * in its key order, so an unedited list compares equal to the stored one. No rows is
 * blank, which clears the list.
 */
export function serializeScadaRows(rows: readonly ScadaRow[]): string {
    if (rows.length === 0) return '';
    return JSON.stringify(
        rows.map((row) => ({
            item_id: row.itemId.trim(),
            signal: row.signal,
            feed_line: row.feedLine.trim() === '' ? null : row.feedLine,
        }))
    );
}

export interface ScadaRowProblems {
    itemId: boolean;
    signal: boolean;
    feedLine: boolean;
}

/**
 * Which parts of each row break the rules validateScadaItems applies to the whole list, so
 * the row at fault can be marked: a malformed id, one used by an earlier row too, an
 * unknown signal, a feed line that is too long.
 */
export function scadaRowProblems(
    rows: readonly ScadaRow[],
    signals: readonly string[]
): ScadaRowProblems[] {
    const counts = new Map<string, number>();
    for (const row of rows) {
        const itemId = row.itemId.trim();
        counts.set(itemId, (counts.get(itemId) ?? 0) + 1);
    }
    return rows.map((row) => {
        const itemId = row.itemId.trim();
        return {
            itemId: !CLARIFY_ITEM_ID_PATTERN.test(itemId) || (counts.get(itemId) ?? 0) > 1,
            signal: !signals.includes(row.signal),
            feedLine: row.feedLine.trim().length > SCADA_FEED_LINE_MAX,
        };
    });
}
